// save-playlist.js — save a list of tracks as a playlist on Spotify or Apple
// Music. First caller: "Save this queue" on the music card, which turns a
// room's Sonos queue back into a playlist after the fact. Built so the
// Playlist Builder's own Save buttons can move onto it (plan phase 1,
// BRIEF-playlist-builder.md §Spotify save).
//
// Spotify writes go straight from the panel with the HA-held token the music
// card already uses (ha-av spotify_token). Proven 2026-10-31: POST
// /me/playlists then POST /playlists/{id}/items succeed in Development Mode;
// the old /tracks path is 403. `public: false` is ignored by Spotify, so
// saved playlists come out public; the UI must not promise private.
//
// Apple writes go through the existing /music-api/apple-save (MUT bridge),
// which re-resolves {title, artist} against Apple's catalogue.
//
// One save per tap and no retries: Spotify's quota is pooled across the
// developer account, so a loop here could throttle HA's own Spotify
// (memory: spotify-rate-limit-cascades-past-the-panel).

const SPOTIFY_API = 'https://api.spotify.com/v1';
const ADD_CHUNK = 100;   // Spotify's per-request cap on /items
export const RESOLVE_CAP = 50; // /music-api/resolve returns at most 50 tracks
export const APPLE_CAP = 50;   // /music-api/apple-save keeps at most 50

// Spotify track id from a sonos.get_queue media_content_id, e.g.
// `x-sonos-spotify:spotify%3atrack%3a<id>?sid=12&...`. Null otherwise.
export function spotifyTrackIdFrom(contentId) {
  if (!contentId) return null;
  let decoded;
  try { decoded = decodeURIComponent(contentId); } catch { decoded = contentId; }
  const m = decoded.match(/spotify:track:([a-zA-Z0-9]+)/);
  return m ? m[1] : null;
}

// Split a Sonos queue into what Spotify can take directly and what needs a
// catalogue match. Order is the queue's. Items with no title (radio streams,
// line-in) can't be matched anywhere and are counted as skipped.
export function planQueue(items) {
  const rows = [];
  let skipped = 0;
  (items || []).forEach((it) => {
    const title = (it?.media_title || '').trim();
    const artist = (it?.media_artist || '').trim();
    const sid = spotifyTrackIdFrom(it?.media_content_id);
    if (!sid && !title) { skipped += 1; return; }
    rows.push({ title, artist, uri: sid ? `spotify:track:${sid}` : null });
  });
  return { rows, skipped };
}

// Fill the non-Spotify rows from a /music-api/resolve result. Each candidate
// was sent with `why` = its row index, which resolve echoes back on the
// matched track, so matches land in queue order whatever resolve dropped.
export function mergeResolved(rows, resolvedTracks) {
  const byIndex = new Map();
  for (const t of resolvedTracks || []) {
    const i = Number(t?.why);
    if (Number.isInteger(i) && t?.uri) byIndex.set(i, t.uri);
  }
  return rows.map((r, i) => (r.uri ? r : { ...r, uri: byIndex.get(i) || null }));
}

export function chunk(list, size = ADD_CHUNK) {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

// "Office queue, 31 October 2026"
export function defaultQueueName(roomName, date = new Date()) {
  const d = `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
  return `${roomName || 'Sonos'} queue, ${d}`;
}

async function haBearer(hass) {
  const auth = hass?.auth;
  if (!auth) throw new Error('Not connected to Home Assistant');
  if (auth.expired) await auth.refreshAccessToken();
  return auth.accessToken;
}

// Match title/artist rows against Spotify via the builder's resolver (app
// token, server-side). Returns the rows with uris filled where matched.
export async function resolveMissing(hass, rows) {
  const want = rows.map((r, i) => ({ r, i })).filter(({ r }) => !r.uri).slice(0, RESOLVE_CAP);
  if (!want.length) return rows;
  const token = await haBearer(hass);
  const res = await fetch('/music-api/resolve', {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify({
      candidates: want.map(({ r, i }) => ({ title: r.title, artist: r.artist, why: String(i) })),
      target_length: RESOLVE_CAP,
      allow_explicit: true,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `resolve ${res.status}`);
  return mergeResolved(rows, data.tracks);
}

// Create a Spotify playlist in the token owner's account and fill it.
// Returns { id, url, count }. Throws Error with `.status` on refusal.
export async function saveToSpotify(getToken, { name, description, uris }) {
  if (!uris?.length) throw new Error('nothing to save');
  const token = await getToken();
  const call = async (path, body) => {
    const res = await fetch(SPOTIFY_API + path, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      const err = new Error(`Spotify ${res.status}${text ? `: ${text.slice(0, 160)}` : ''}`);
      err.status = res.status;
      throw err;
    }
    return res.json();
  };
  const created = await call('/me/playlists', { name, description, public: false });
  for (const part of chunk(uris)) {
    await call(`/playlists/${created.id}/items`, { uris: part });
  }
  return { id: created.id, url: created.external_urls?.spotify || null, count: uris.length };
}

// Save {title, artist} rows to the Apple Music library via the MUT bridge.
// Returns the apple-save JSON ({ ok, playlist, misses }) or throws an Error
// whose `.code` is apple-save's typed error.
export async function saveToApple(hass, { name, rows }) {
  const token = await haBearer(hass);
  const candidates = rows.filter((r) => r.title).slice(0, APPLE_CAP)
    .map((r) => ({ title: r.title, artist: r.artist }));
  const res = await fetch('/music-api/apple-save', {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify({ candidates, name, target_length: candidates.length, allow_explicit: true }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.ok && data.ok) return data;
  const err = new Error(data.error || `apple save ${res.status}`);
  err.code = data.error;
  throw err;
}
